// New-project / topic input with Document_Type + Output_Language selection.

import React from "react";
import { useNavigate } from "react-router-dom";
import type { DocumentType, OutputLanguage } from "@asmg/shared";
import { DOCUMENT_TYPES, OUTPUT_LANGUAGES } from "@asmg/shared";
import { api } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";

const LANG_LABELS: Record<OutputLanguage, string> = {
  hinglish: "Hinglish",
  english: "English",
  hindi: "Hindi",
};

export function NewProjectView() {
  const [topic, setTopic] = React.useState("");
  const [documentType, setDocumentType] = React.useState<DocumentType>("study-material");
  const [language, setLanguage] = React.useState<OutputLanguage>("hinglish");
  const [error, setError] = React.useState<unknown>(null);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();

  const topicEmpty = topic.trim().length === 0;

  async function onCreate() {
    if (topicEmpty) {
      setError(new Error("Please enter a non-empty topic."));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const project = await api.createProject({ topic: topic.trim(), documentType, language });
      navigate(`/project/${project.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container">
      <h2>New Project</h2>
      <div className="card">
        <label htmlFor="topic">Topic</label>
        <textarea
          id="topic"
          placeholder="e.g. HR Executive — Payroll & Employee Data Management"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />

        <label htmlFor="doctype">Document Type</label>
        <select
          id="doctype"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value as DocumentType)}
        >
          {DOCUMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <label htmlFor="lang">Output Language</label>
        <select
          id="lang"
          value={language}
          onChange={(e) => setLanguage(e.target.value as OutputLanguage)}
        >
          {OUTPUT_LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {LANG_LABELS[l]}
            </option>
          ))}
        </select>

        <ErrorBanner error={error} onRetry={onCreate} />

        <div className="row" style={{ marginTop: 14 }}>
          <Button onClick={onCreate} loading={loading} disabled={topicEmpty}>
            Create & Continue
          </Button>
          {topicEmpty && <span className="muted">Enter a topic to continue.</span>}
        </div>
      </div>
    </div>
  );
}
